/* eslint no-sequences: 0 */
import React from 'react';
// import PropTypes from 'prop-types';
import classnames from 'classnames/bind';

import { Button, Triggered, ALIGNMENTS } from '../flyouts';
import List from '../tables/List';
import { SmallContext } from '../utils';

import Select, { label } from './Select';
import Search from './Search';

import styles from './SelectFilterable.less';

const cx = classnames.bind(styles);

const empty = (x) => !x || x.length === 0;
const escape = (s) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

const getTarget = (el, className) =>
  (!el || el.classList.contains(className) ? el : getTarget(el.parentNode, className)) || el;

/**
 * **Access:**
 * ```js
 * import { SelectFilterable } from '@iss/client-react-components'
 * ```
 *
 * Provides a filterable select menu. Same props & api as Select
 */
export default class SelectFilterable extends Select {
  state = {};

  handleFilter = (v) => {
    this.setState({ filter: v });
  };

  setup = (el) => {
    this.search = el;
    if (el) setImmediate(() => el.focus());
    else this.setState({ filter: null });
  };

  render() {
    const {
      value: selected,
      state: { filter },
      props: { placeholder, kind, values, size = 30, disabled, ...props },
    } = this;

    const small = SmallContext.isSmall(this);
    const ref = React.createRef();
    const dismiss = () => ref.current.dismiss();
    const selectedLabel = label(selected) || placeholder || '\xa0\xa0';

    const button = (
      <Button
        {...props}
        role="listbox"
        kind={kind}
        small={small}
        ref={this.trigger}
        onKeyDown={this.triggerKeys}
        disabled={disabled}
      >
        {selectedLabel}
      </Button>
    );

    const filterFn = empty(filter) ? null : RegExp.prototype.test.bind(new RegExp(escape(filter), 'i'));

    const items = !filterFn ? values : values.filter((x) => filterFn(label(x)));

    return (
      <Triggered trigger={button} horizontalAlign={ALIGNMENTS.LEFT_OR_RIGHT} ref={ref}>
        <>
          <List
            items={items}
            rowClasses={(x) => cx({ selected: selected === x })}
            rowLabel={(x) => '' + (x.label || x)}
            classes={{
              container: cx('container'),
              controls: cx('controls'),
              row: cx('row'),
            }}
            onKeyDown={this.handleKeys}
            onItemClick={(item, e) => {
              this.handleSelect(e, item);
              dismiss();
            }}
          >
            <Search placeholder="Search..." size={size} ref={this.setup} value={filter} onChange={this.handleFilter} />
          </List>
        </>
      </Triggered>
    );
  }

  handleKeys = (e) => {
    if (!e || !/Arrow(Up|Down)/i.test(e.key)) {
      this.search.focus();
      return;
    }

    const direction = /Up/i.test(e.key) ? -1 : 1;

    e.stopPropagation();
    e.preventDefault();

    const el = getTarget(e.target, cx('container'));
    const focused = el.querySelector('[role=option]:focus');
    const options = Array.from(el.querySelectorAll('[role=option]'));

    if (!focused && direction > 0) {
      return options[0].focus();
    }

    const index = options.indexOf(focused) + direction;

    if (options[index]) {
      options[index].focus();
    } else {
      this.search.focus();
    }
  };
}
